import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
    ClientPortal,
    Currency,
    DisplayAddress,
    DisplayClient,
    DisplayContact,
    DisplayCourier,
    DisplayDepartment,
    DisplayNote,
    DisplayPhone,
    DisplayShippingAccount,
    DisplayTag,
    DisplayTax,
    DisplayTerms,
    DisplayUser,
    isClientPortal,
    isCurrency,
    isDisplayAddress,
    isDisplayClient,
    isDisplayContact,
    isDisplayCourier,
    isDisplayDepartment,
    isDisplayNote,
    isDisplayPhone,
    isDisplayShippingAccount,
    isDisplayTag,
    isDisplayTax,
    isDisplayTerms,
    isDisplayUser,
    isMessageParentType,
    isNoteFile,
    isNoteParentType,
    MessageParentType,
    NoteFile,
    NoteParentType,
    validateClientPortal,
    validateCurrency,
    validateDisplayAddress,
    validateDisplayClient,
    validateDisplayContact,
    validateDisplayCourier,
    validateDisplayDepartment,
    validateDisplayNote,
    validateDisplayPhone,
    validateDisplayShippingAccount,
    validateDisplayTag,
    validateDisplayTax,
    validateDisplayTerms,
    validateDisplayUser,
} from '../types';
import { DisplayAccountStatus, isDisplayAccountStatus, validateDisplayAccountStatus } from "../types/account_status";
import { DisplayIndustry, isDisplayIndustry } from "../types/industry";
import { popups } from "../components/client-page/types";
import { isNumber, isString } from "../types/type_guards";
import { isZeroDate, oauth } from "../utils";
import { DateRange } from "@commonsku/styles";
import { updateNoteSuccess } from "./notes";
import { ADD_MESSAGE_SUCCESS } from "../actions/message";
import { Draft as WritableDraft } from "immer";

declare global {
    interface Window {
        initialState: unknown;
    }
}

export interface ClientDetailsState {
    loading: boolean;
    currentPopup: keyof typeof popups | null;
    editingDetails: boolean;
    notes: readonly DisplayNote[];
    error?: string;
    oldestNoteLoaded: boolean;
}

export const adaptUser = (user: any): DisplayUser => {
    const adaptedUser = {
        id: user.user_id,
        firstName: user.user_first_name ?? undefined,
        lastName: user.user_last_name ?? undefined,
        avatarImagePath: user.user_avatar_image_path?.length > 0
            ? user.user_avatar_image_path
            : undefined,
        mask: user.mask ?? undefined,
        userType: user.user_type ?? undefined,
    };

    if (!isDisplayUser(adaptedUser)) {
        throw new Error("Did not receive a valid DisplayUser");
    }

    validateDisplayUser(adaptedUser);
    return adaptedUser;
};

export const adaptTax = (tax: any): DisplayTax => {
    const adaptedTax = {
        id: tax.tax_id,
        label: tax.label,
        percent: parseFloat(tax.percent),
    };
    if (!isDisplayTax(adaptedTax)) {
        throw new Error("Did not receive a valid DisplayTax");
    }

    validateDisplayTax(adaptedTax);
    return adaptedTax;
};

export const adaptDepartment = (department: any): DisplayDepartment => {
    const adaptedDepartment = {
        id: department.department_id,
        name: department.department_name,
        ordering: Number(department.ordering),
    };

    if (!isDisplayDepartment(adaptedDepartment)) {
        throw new Error("Did not receive a valid DisplayDepartment");
    }

    validateDisplayDepartment(adaptedDepartment);
    return adaptedDepartment;
};

export const adaptTag = (tag: any): DisplayTag => {
    const adaptedTag = {
        id: tag.tag_id,
        label: tag.label,
    };

    if (!isDisplayTag(adaptedTag)) {
        throw new Error("Did not receive a valid DisplayTag");
    }

    validateDisplayTag(adaptedTag);
    return adaptedTag;
};

export const adaptPhone = (phone: any): DisplayPhone => {
    let number: string;

    if (
        isString(phone.phone_number) &&
        phone.phone_number.length > 0 &&
        isString(phone.phone_extension) &&
        phone.phone_extension.length > 0
    ) {
        number = `${phone.phone_number} ext. ${phone.phone_extension}`;
    }
    else if (isString(phone.phone_number)) {
        number = phone.phone_number;
    }
    else {
        number = phone.phone_extension;
    }

    const adaptedPhone = {
        id: phone.phone_id,
        type: phone.phone_type,
        number: number,
    };

    if (!isDisplayPhone(adaptedPhone)) {
        throw new Error("Did not receive a valid DisplayPhone");
    }

    validateDisplayPhone(adaptedPhone);
    return adaptedPhone;
};

export const adaptAddress = (address: any): DisplayAddress => {
    const adaptedAddress = {
        id: address.address_id,
        type: address.address_type,
        name: address.address_name ?? undefined,
        company: address.address_company ?? '',
        city: address.address_city ?? '',
        state: address.address_state ?? '',
        country: address.address_country ?? '',
        postal: address.address_postal ?? '',
        address_line_1: address.address_line_1 ?? '',
        address_line_2: address.address_line_2 ?? '',
        address_line_3: address.address_line_3 ?? '',
        address_line_4: address.address_line_4 ?? '',
        active: !!address.active,
    };

    if (!isDisplayAddress(adaptedAddress)) {
        throw new Error("Did not receive a valid DisplayAddress");
    }

    validateDisplayAddress(adaptedAddress);
    return adaptedAddress;
};

export const adaptCourier = (courier: any): DisplayCourier => {
    const adaptedCourier = {
        id: courier.courier_id,
        name: courier.courier_name,
        label: courier.courier_label,
        active: !!courier.active,
    };

    if (!isDisplayCourier(adaptedCourier)) {
        throw new Error("Did not receive a valid DisplayCourier");
    }

    validateDisplayCourier(adaptedCourier);
    return adaptedCourier;
};

export const adaptShippingAccount = (shippingAccount: any, couriers: DisplayCourier[]): DisplayShippingAccount => {
    const adaptedCourier = couriers.find(courier => courier.id === shippingAccount.courier_id);

    if (adaptedCourier === undefined) {
        throw new Error(`Could not find courier with ID ${shippingAccount.courier_id}`);
    }

    const adaptedShippingAccount = {
        id: shippingAccount.third_party_account_id,
        name: shippingAccount.account_name,
        number: shippingAccount.account_number,
        postal: shippingAccount.account_postal,
        active: !!shippingAccount.active,
        courier: adaptedCourier,
    };

    if (!isDisplayShippingAccount(adaptedShippingAccount)) {
        throw new Error("Did not receive a valid DisplayShippingAccount");
    }

    validateDisplayShippingAccount(adaptedShippingAccount);
    return adaptedShippingAccount;
};

export const adaptContact = (
    contact: any,
    departments: DisplayDepartment[],
    addresses: DisplayAddress[],
    tags: DisplayTag[],
    phones: DisplayPhone[],
): DisplayContact => {
    const defaultDepartment: DisplayDepartment = {
        id: '0',
        name: 'Unknown',
        ordering: Infinity,
    };

    const adaptedPhoneIds = (contact.phones ?? []).length > 0 && typeof contact.phones[0] === 'object'
        ? contact.phones.map(phone => phone.phone_id)
        : (contact.phones ?? []);

    const adaptedContact = {
        id: contact.contact_id ?? contact.company_contact_id,
        firstName: contact.contact_first_name,
        lastName: contact.contact_last_name,
        email: contact.contact_email ?? '',
        position: contact.contact_position ?? '',
        department: departments.find(department => department.id === (contact.contact_department_id ?? contact.department_id))
            ?? defaultDepartment,
        noMarketing: !!contact.contact_no_marketing,
        twitter: contact.contact_twitter ?? undefined,
        facebook: contact.contact_facebook ?? undefined,
        linkedin: contact.contact_linkedin ?? undefined,
        skype: contact.contact_skype ?? undefined,
        address: (contact.contact_address_id ?? contact.contact_default_address_id)
            ? addresses.find(address => address.id === (contact.contact_address_id ?? contact.contact_default_address_id))
            : undefined,
        tags: (contact.tags ?? []).map(id => tags.find(tag => tag.id === id)).filter(tag => tag !== undefined),
        phones: adaptedPhoneIds.map(id => phones.find(phone => phone.id === id)),
        hubspotSyncStatus: contact.hubspot_sync_status
            ? contact.hubspot_sync_status
            : undefined,
        active: !!contact.active,
        companyContactId: contact.company_contact_id,
    };

    if (!isDisplayContact(adaptedContact)) {
        throw new Error(`Did not receive a valid DisplayContact [${contact.contact_id ?? contact.company_contact_id}]`);
    }

    validateDisplayContact(adaptedContact);
    return adaptedContact;
};

export const adaptAccountStatus = (accountStatus: any): DisplayAccountStatus => {
    const adaptedAccountStatus = {
        id: accountStatus.account_status_id,
        name: accountStatus.account_status_name,
    };

    if (!isDisplayAccountStatus(adaptedAccountStatus)) {
        throw new Error("Did not receive a valid DisplayAccountStatus");
    }

    validateDisplayAccountStatus(adaptedAccountStatus);
    return adaptedAccountStatus;
};

export const adaptTerms = (terms: any): DisplayTerms => {
    const adaptedTerms = {
        id: terms.terms_id,
        name: terms.terms_name,
    };

    if (!isDisplayTerms(adaptedTerms)) {
        throw new Error("Did not receive a valid DisplayTerms");
    }

    validateDisplayTerms(adaptedTerms);
    return adaptedTerms;
};

export const adaptCurrency = (currency: any): Currency => {
    const adaptedCurrency = {
        id: currency.currency_id,
        country: currency.currency_country,
        name: currency.currency_name,
        symbol: currency.currency_symbol,
    };

    if (!isCurrency(adaptedCurrency)) {
        throw new Error("Did not receive a valid Currency");
    }

    validateCurrency(adaptedCurrency);
    return adaptedCurrency;
};

export const adaptClientPortal = (clientPortal: any): ClientPortal => {
    const adaptedPortal = {
        id: clientPortal.client_portal_id,
    };

    if (!isClientPortal(adaptedPortal)) {
        throw new Error("Did not receive a valid ClientPortal");
    }

    validateClientPortal(adaptedPortal);
    return adaptedPortal;
};

export const adaptClient = (
    client: any,
    users: DisplayUser[],
    contacts: DisplayContact[],
    accountStatuses: DisplayAccountStatus[],
    terms: DisplayTerms[],
    taxes: DisplayTax[],
    currencies: Currency[],
    tags: DisplayTag[],
    phones: DisplayPhone[],
    portals: ClientPortal[],
    industries: DisplayIndustry[],
): DisplayClient => {
    const adaptedClient = {
        id: client.client_id,
        name: client.client_name,
        email: client.client_email,
        industry: industries
            .find(industry => industry.id === client.industry_id),
        tenantAccountNumber: isString(client.client_tenant_account_number) && client.client_tenant_account_number.length > 0
            ? client.client_tenant_account_number
            : null,
        clientRep: users.find(user => user.id === client.sales_rep_id) ?? null,
        primaryContact: contacts.find(contact => contact.id === client.primary_contact_id) ?? null,
        status: client.account_status_id !== null
            ? accountStatuses.find(status => status.id === client.account_status_id)
            : null,
        defaultTerms: terms
            .find(term => term.id === client.default_terms_id),
        defaultTax: taxes
            .find(tax => tax.id === client.default_tax_id),
        defaultCurrency: currencies
            .find(currency => currency.id === client.default_currency_id),
        tags: (client.tags ?? [])
            .map(id => tags.find(tag => tag.id === id)),
        phones: (client.phones ?? [])
            .filter(phoneOrId => phoneOrId !== null)
            .map(phoneOrId => phones.find(other => (typeof phoneOrId === 'object' && 'phone_id' in phoneOrId)
                ? phoneOrId.phone_id === other.id
                : phoneOrId === other.id
            ))
            .filter(phone => phone !== undefined),
        minMargin: client.client_order_margin_minimum !== null
            ? Number(client.client_order_margin_minimum)
            : null,
        portal: client.client_portal_id !== null
            ? portals.find(portal => portal.id === client.client_portal_id)
            : null,
        salesToDate: Number(client.sales_to_date),
        salesTarget: Number(client.sales_target),
        avalaraEntityUseCode: isString(client.avalara_entity_use_code) && client.avalara_entity_use_code.length > 0
            ? client.avalara_entity_use_code
            : null,
        creditHold: !!client.credit_hold,
        parentClientId: isString(client.parent_client_id) && client.parent_client_id.length > 0
            ? client.parent_client_id
            : null,
        parentClientName: isString(client.parent_client_name)
            ? client.parent_client_name
            : null,
        qboCustomerRef: isNumber(client.qbo_customer_ref)
            ? `${client.qbo_customer_ref}`
            : null,
        xeroContactId: isString(client.xero_contact_id) && client.xero_contact_id.length > 0
            ? client.xero_contact_id
            : null,
        profile: isString(client.profile)
            ? client.profile
            : null,
        website: isString(client.client_website)
            ? client.client_website
            : null,
        facebook: isString(client.client_facebook)
            ? client.client_facebook
            : null,
        twitter: isString(client.client_twitter)
            ? client.client_twitter
            : null,
        commissionClientRateId: isString(client.commission_client_rate_id)
            ? client.commission_client_rate_id
            : null,
    };

    if (!isDisplayClient(adaptedClient)) {
        throw new Error("Did not receive a valid DisplayClient");
    }

    validateDisplayClient(adaptedClient);
    return adaptedClient;
};

export const adaptIndustry = (industry: any): DisplayIndustry => {
    const adaptedIndustry = {
        id: industry.industry_id,
        name: industry.industry_name,
    };

    if (!isDisplayIndustry(adaptedIndustry)) {
        throw new Error("Did not receive a valid DisplayIndustry");
    }

    return adaptedIndustry;
};

const adaptNoteFile = (file: any): NoteFile => {
    const adaptedFile = {
        id: file.file_id,
        displayName: file.file_display_name,
        fileType: file.file_type,
        filePath: file.file_path,
        fileSize: file.file_size,
    };

    if (!isNoteFile(adaptedFile)) {
        throw new Error("Did not receive a valid NoteFile");
    }

    return adaptedFile;
};

const parseNoteDate = (dateValue: any): Date | undefined => {
    if (typeof dateValue === 'string') {
        return new Date(dateValue);
    }

    if (typeof dateValue === 'number') {
        return new Date(dateValue * 1000);
    }

    console.warn('Unexpected date format:', dateValue);
    return undefined;
};

const extractNoteParentTypes = (note: any): {
    parentType?: MessageParentType;
    noteParentType?: NoteParentType;
} => {
    const parentTypeFields = [
        note.parent_type,
        note.note?.parent_type,
        note.message_parent_type,
        note.note?.message_parent_type
    ];

    const noteParentTypeFields = [
        note.parent_type,
        note.note?.parent_type,
        note.details_parent_type,
        note.note?.details_parent_type,
        note.note_parent_type,
        note.note?.note_parent_type
    ];

    const parentType = parentTypeFields
        .find(type => typeof(type) === 'string' && isMessageParentType(type.toUpperCase()));

    const noteParentType = noteParentTypeFields
        .find(type => typeof(type) === 'string' && isNoteParentType(type.toUpperCase()));

    return {
        parentType: parentType?.toUpperCase(),
        noteParentType: noteParentType?.toUpperCase(),
    };
};

const adaptNote = (note: any, users: readonly DisplayUser[], contacts: readonly DisplayContact[]): DisplayNote | null => {
    const authorId = note.actor.user_id ?? note.actor;

    const comments = note.replies
        ? note.replies.map((comment: any) => adaptNote(comment, users, contacts))
        : [];

    const reminderUserId = note.note?.reminder_user
        ? note.note?.reminder_user
        : note.reminder_user;

    const reminder = {
        reminderUser: users.find(user => user.id === reminderUserId) ??
        (reminderUserId && note.reminder_user_first_name && note.reminder_user_last_name
            ? {
                id: reminderUserId,
                firstName: note.reminder_user_first_name,
                lastName: note.reminder_user_last_name,
            } as DisplayUser
            : undefined
        ),
        reminderDate: !isZeroDate(note.note?.date_reminder)
            ? new Date(note.note?.date_reminder)
            : !isZeroDate(note.date_reminder)
                ? new Date(note.date_reminder)
                : undefined,
        dateCompleted: !isZeroDate(note.note?.date_complete)
            ? new Date(note.note?.date_complete)
            : !isZeroDate(note.date_complete)
                ? new Date(note.date_complete)
                : undefined,
        completed: note.note?.reminder_complete
            ? !!note.note?.reminder_complete
            : !!note.reminder_complete,
    };

    const baseNoteType = (note.note_type ?? note.note?.note_type) ?? 'NOTE';

    const adaptedNote = {
        type: baseNoteType,
        ...extractNoteParentTypes(note),
        id: (note.note?.note_id) ?? note.note_id ?? note.message_id,
        messageId: note.message_id,
        message: note.text ?? note.message_text,
        emailBodyTruncated: note.email_body_truncated === true,
        emailId: note.details_type === 'EMAIL' ? note.email?.email_id ?? note.details_id : undefined,
        author: users.find(user => user.id === authorId),
        creationDate: parseNoteDate(note.date ?? note.date_created),
        pinned: note.pinned ? true : false,
        comments: comments,
        contact: note.note?.contact_id
            ? contacts.find(contact => contact.id === note.note?.contact_id)
            : undefined,
        reminder: (reminder.reminderDate && reminder.reminderUser) ? reminder : undefined,
        files: (note.files ?? []).map(file => adaptNoteFile(file)),
    };

    if (!isDisplayNote(adaptedNote)) {
        console.warn("Did not receive a valid DisplayNote");
        return null;
    }

    validateDisplayNote(adaptedNote);
    return adaptedNote;
};

const getInitialState = (): ClientDetailsState => {
    return {
        loading: false,
        currentPopup: null,
        editingDetails: false,
        notes: [],
        oldestNoteLoaded: false,
    };
};

export const fetchNotes = createAsyncThunk<
    {
        notes: DisplayNote[],
        oldestNoteLoaded: boolean,
    },
    {
        client_id: string,
        user_id: string,
        users: readonly DisplayUser[],
        contacts: readonly DisplayContact[],
        minimumNotes?: number,
        getNewNotes?: boolean,
        dateRange?: DateRange,
    },
    {
        state: {
            clientDetails: ClientDetailsState,
        },
    }
>(
    'clientDetails/fetchNotes',
    async ({ client_id, user_id, users, contacts, minimumNotes = 1, getNewNotes = false, dateRange={category: '', startDate: null, endDate: null} }, { getState }) => {
        const notes = [];

        const {
            clientDetails: {
                notes: existingNotes,
                oldestNoteLoaded,
            },
        } = getState();

        if (oldestNoteLoaded && !getNewNotes) {
            return {
                notes,
                oldestNoteLoaded,
            };
        }

        const data = {
            user_id,
            child_id: client_id,
            type: "CLIENT",
            email_previews: true,
            show_all: true,
            date_range: 'all',
            filter: 'all',
        };

        if ((dateRange?.category ?? '') !== '') {
            data['date_range'] = dateRange.category;
            if (dateRange.startDate) {
                data['start_date'] = dateRange.startDate;
            }
            if (dateRange.endDate) {
                data['end_date'] = dateRange.endDate;
            }
        }

        if (getNewNotes && existingNotes.length > 0 && dateRange?.category === '') {
            data['start_date'] = existingNotes[0].creationDate.getTime() / 1000 + 1;
        } else if (existingNotes.length > 0) {
            data['since'] = existingNotes[existingNotes.length - 1].creationDate.getTime() / 1000;
        }

        let notesAdded = -1;

        while (notes.length < minimumNotes && notesAdded !== 0) {
            const { json: { newsfeed } } = await oauth('GET', 'newsfeed/client', data, null, null, null, false);

            const newsfeedUsers: Record<string, DisplayUser> = {};

            newsfeed
                .reduce((actors: any[], note: any) => {
                    actors.push(note.actor);
                    actors.push(...note.replies.map((reply: any) => reply.actor));
                    return actors;
                }, [])
                .forEach((actor: any) => {
                    if (typeof actor === 'string' && !(actor in newsfeedUsers)) {
                        newsfeedUsers[actor] = {
                            id: actor,
                            firstName: actor,
                        };
                        return;
                    }

                    if (typeof actor !== 'object' || !('user_id' in actor) || actor.user_id in newsfeedUsers) {
                        return;
                    }

                    newsfeedUsers[actor.user_id] = {
                        id: actor.user_id,
                        firstName: actor.user_first_name,
                        lastName: actor.user_last_name,
                        avatarImagePath: actor.user_image,
                        userType: actor.user_type,
                    };
                });

            const newNotes = newsfeed.map(
                (note: any) => adaptNote(
                    note,
                    [...users, ...Object.values(newsfeedUsers)],
                    contacts,
                )
            ).filter(note => note != null);

            notesAdded = newNotes.length;
            notes.push(...newNotes);

            if (newNotes.length > 0) {
                data['since'] = newNotes[newNotes.length - 1].creationDate.getTime() / 1000;
            }
        }

        return {
            notes,
            oldestNoteLoaded: (!getNewNotes)
                ? notesAdded === 0
                : oldestNoteLoaded,
        };
    },
);

export const editNote = createAsyncThunk(
    'clientDetails/editNote',
    async ({current_note, text}: {current_note: DisplayNote, text: string}) => {
        const { json } = await oauth('PUT', `note/${current_note.id}`, {
            message_text: text
        });
        return {current_note, text: json?.note?.message_text ?? text};
    }
);

export const completeTask = createAsyncThunk(
    'clientDetails/completeTask',
    async ({ note_id, completed }: { note_id: string, completed: boolean }) => {
        await oauth('PUT', `note/${note_id}`, {
            reminder_complete: completed ? 1 : 0,
            date_complete: new Date(),
        });
        return { note_id, completed };
    }
);

export const pinNote = createAsyncThunk(
    'clientDetails/pinNote',
    async ({ message_id, pinned }: { message_id: string, pinned: boolean }) => {
        await oauth('PUT', `message/${message_id}`, {
            pinned: pinned ? 1 : 0,
        });
        return { message_id, pinned };
    }
);

export const restoreJob = createAsyncThunk(
    'clientDetails/restoreJob',
    async (job_id: string) => {
        await oauth('PUT', `job/${job_id}`, {
            active: 1,
        });
        return job_id;
    }
);

export const restoreOrder = createAsyncThunk(
    'clientDetails/restoreOrder',
    async (order_id: string) => {
        await oauth('PUT', `order/${order_id}`, {
            active: 1,
        });
        return order_id;
    }
);

const sortNotes = (notes) => {
    return [...notes].sort((a, b) => {
        if (b.pinned && !a.pinned) return 1;
        if (a.pinned && !b.pinned) return -1;
        return b.creationDate.getTime() - a.creationDate.getTime();
    });
};

const addNoteToState = (state: WritableDraft<ClientDetailsState>, noteData: any) => {
    const actor = noteData.actor || {
        user_id: noteData.user_id,
        user_first_name: noteData.user_first_name,
        user_last_name: noteData.user_last_name,
        user_image: noteData.user_image,
        user_type: noteData.user_type,
    };

    const author: DisplayUser = {
        id: actor.user_id,
        firstName: actor.user_first_name,
        lastName: actor.user_last_name,
        avatarImagePath: actor.user_image,
        userType: actor.user_type,
    };

    const adaptedNote = adaptNote(noteData, [author], []);

    if (adaptedNote == null) {
        return;
    }

    const noteExists = state.notes.some(note => note.id === adaptedNote.id);
    if (noteExists) {
        return;
    }

    state.notes.push(adaptedNote as WritableDraft<DisplayNote>);
    state.notes = sortNotes(state.notes);
};

const clientDetailsSlice = createSlice({
    name: "clientDetails",
    initialState: getInitialState() satisfies ClientDetailsState as ClientDetailsState,
    reducers: {
        setLoading(state, action: PayloadAction<boolean>) {
            state.loading = action.payload;
        },
        showPopup(state, action: PayloadAction<keyof typeof popups>) {
            state.currentPopup = action.payload;
        },
        closePopup(state) {
            state.currentPopup = null;
            state.editingDetails = false;
        },
        setEditingDetails(state, action: PayloadAction<boolean>) {
            state.editingDetails = action.payload;
        },
        deleteNote(state, action: PayloadAction<DisplayNote>) {
            const noteIndex = state.notes?.findIndex(note => note.id === action.payload.id);

            if (noteIndex !== undefined && noteIndex >= 0) {
                state.notes?.splice(noteIndex, 1);
                return;
            }

            state.notes.forEach(note => {
                const commentIndex = note.comments.findIndex(comment => comment.id === action.payload.id);

                if (commentIndex !== undefined && commentIndex >= 0) {
                    note.comments.splice(commentIndex, 1);
                    return;
                }
            });
        },
        clearNotes(state) {
            state.notes = [];
        },
    },
    extraReducers: (builder) => {
        builder.addCase(fetchNotes.fulfilled, (state, action) => {
            action.payload.notes.forEach(note => {
                const existingNote = state.notes?.find(existingNote => existingNote.id === note.id);

                if (existingNote) {
                    existingNote.comments = [...note.comments] as Array<WritableDraft<DisplayNote>>;
                    return;
                }

                state.notes.push(note as WritableDraft<DisplayNote>);
            });
            state.notes = sortNotes(state.notes);
            state.loading = false;
            state.oldestNoteLoaded = action.payload.oldestNoteLoaded;
        });

        builder.addCase(pinNote.fulfilled, (state, action) => {
            const { message_id, pinned } = action.payload;
            const note = state.notes?.find(note => note.messageId === message_id);

            if (note) {
                note.pinned = pinned;
                state.notes = sortNotes(state.notes);
            }
        });

        builder.addCase(completeTask.fulfilled, (state, action) => {
            const { note_id, completed } = action.payload;
            const note = state.notes?.find(note => note.id === note_id);

            if (note?.reminder) {
                note.reminder.completed = completed;
                note.reminder.dateCompleted = new Date();
            }
        });

        builder.addCase(editNote.fulfilled, (state, action) => {
            const { current_note, text } = action.payload;
            const note = state.notes?.find(note => note.id === current_note.id);

            if (note) {
                note.message = text;
            }
        });

        builder.addCase(updateNoteSuccess, (state, action) => {
            const updatedNote = action.payload;
            addNoteToState(state, updatedNote);
        });

        builder.addMatcher(
            (action) => action.type === ADD_MESSAGE_SUCCESS,
            (state, action: PayloadAction<any>) => {
                const messageData = action.payload?.message;
                if (!messageData) {
                    return;
                }

                addNoteToState(state, messageData);
            }
        );
    }
});

export const getClientDetailsState =
    (state): ClientDetailsState => state.clientDetails;

export const {
    setLoading,
    showPopup,
    closePopup,
    setEditingDetails,
    deleteNote,
    clearNotes,
} = clientDetailsSlice.actions;

export default clientDetailsSlice.reducer;
